/**
 * Brazilian CPF formatting and validation helpers.
 */

export const cleanCpf = (value: string): string => value.replace(/\D/g, "");

export const formatCpf = (value: string): string => {
  const digits = cleanCpf(value).slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

/** Privacy mask for lists/details: only the middle 3 digits are visible. */
export const maskCpfForDisplay = (value: string): string => {
  const digits = cleanCpf(value);
  if (digits.length < 6) return "***.***.***-**";
  return `***.${digits.slice(3, 6)}.***-**`;
};

export const handleCpfInputChange = (
  value: string,
  onChange: (formatted: string) => void
): void => {
  onChange(formatCpf(value));
};

export const isValidCpf = (value: string): boolean => {
  const cpf = cleanCpf(value);
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  const calcDigit = (base: string, factor: number): number => {
    let sum = 0;
    for (let i = 0; i < base.length; i += 1) {
      sum += Number(base[i]) * (factor - i);
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  const d1 = calcDigit(cpf.slice(0, 9), 10);
  const d2 = calcDigit(cpf.slice(0, 10), 11);
  return d1 === Number(cpf[9]) && d2 === Number(cpf[10]);
};

export const validateCpf = (
  value: string
): { isValid: boolean; error?: string } => {
  const trimmed = value.trim();
  if (!trimmed) return { isValid: true };

  const digits = cleanCpf(trimmed);
  if (digits.length !== 11) {
    return { isValid: false, error: "CPF deve ter 11 dígitos" };
  }
  if (!isValidCpf(digits)) {
    return { isValid: false, error: "CPF inválido" };
  }
  return { isValid: true };
};
